import {
  WidgetAutocheck,
  WidgetFees,
} from "@redotech/redo-storefront-widget-common/widget";
import {
  ExtensionShopifyClient,
  ShopifyClientType,
  ShopifyProductJS,
  ShopifyProducts,
} from "@redotech/shopify-client/ajax";

import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { CommercecloudStorefrontClient } from "../client/commercecloud-storefront";
import { LightopiaStorefrontClient } from "../client/lightopia";
import { CoverageNotShowingReason } from "../storefront-debugger/coverage-debug-init";

const REDO_PRODUCTS_CACHE_KEY = "redo_products";

interface CachedRedoProducts {
  shopifyProducts: ShopifyProducts;
  currency?: string;
}

function getCurrentCurrency(): string | undefined {
  if (!!globalThis.Tapcart && !globalThis.Shopify) {
    return globalThis.Tapcart.variables.cart.currency;
  }
  return globalThis.Shopify?.currency?.active;
}

async function getRedoProducts({
  autocheck,
  fees,
  storefrontClient,
}: {
  autocheck: WidgetAutocheck;
  fees: WidgetFees;
  storefrontClient: ExtensionShopifyClient;
}) {
  let shopifyProducts: ShopifyProducts = {};
  if (autocheck.paidModel !== "merchant_paid") {
    if (
      autocheck.packageProtection.splitProducts &&
      fees.returnProduct?.handle &&
      fees.packageProduct?.handle &&
      fees.bothProduct?.handle
    ) {
      const promises = [
        storefrontClient.productGetJS(fees.returnProduct.handle),
        storefrontClient.productGetJS(fees.packageProduct.handle),
        storefrontClient.productGetJS(fees.bothProduct.handle),
      ];

      const products: (ShopifyProductJS | undefined)[] =
        await Promise.all(promises);

      for (const product of products) {
        if (!product) {
          continue;
        }
        product.variants.sort((a, b) => a.price - b.price);
      }

      const returnShopifyProduct = products[0];
      const packageShopifyProduct = products[1];
      const bothShopifyProduct = products[2];
      const mainShopifyProduct =
        bothShopifyProduct || returnShopifyProduct || packageShopifyProduct;

      shopifyProducts = {
        mainShopifyProduct,
        returnShopifyProduct,
        packageShopifyProduct,
        bothShopifyProduct,
      };
    } else if (globalThis.redoStorefront?.type === "commercecloud") {
      shopifyProducts = {
        mainShopifyProduct: CommercecloudStorefrontClient.getProductJsFromFees({
          fees,
        }),
      };
    } else if (
      storefrontClient.type === ShopifyClientType.LIGHTOPIA ||
      // Miva has no storefront product API, so build from /fees like Lightopia.
      storefrontClient.type === ShopifyClientType.MIVA
    ) {
      shopifyProducts = {
        mainShopifyProduct: LightopiaStorefrontClient.getProductJsFromFees({
          fees,
        }),
      };
    } else {
      const product = await storefrontClient.productGetJS(fees.handle);
      product.variants.sort((a, b) => a.price - b.price);
      shopifyProducts = { mainShopifyProduct: product };
    }
  }

  const cached: CachedRedoProducts = {
    shopifyProducts,
    currency: getCurrentCurrency(),
  };
  setLocalStorageWithExpiry(REDO_PRODUCTS_CACHE_KEY, cached, 0.1);
  return shopifyProducts;
}

export async function getRedoProductsCache({
  autocheck,
  fees,
  storefrontClient,
}: {
  autocheck: WidgetAutocheck;
  fees: WidgetFees;
  storefrontClient: ExtensionShopifyClient;
}): Promise<ShopifyProducts> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const cached = getLocalStorageWithExpiry(REDO_PRODUCTS_CACHE_KEY) as
    | CachedRedoProducts
    | ShopifyProducts
    | null;
  const cachedProducts: ShopifyProducts | undefined =
    cached && "shopifyProducts" in cached
      ? cached.shopifyProducts
      : ((cached as ShopifyProducts | null) ?? undefined);
  const cachedCurrency: string | undefined =
    cached && "shopifyProducts" in cached ? cached.currency : undefined;
  const shopCurrency = getCurrentCurrency();

  // Skip the cache if it was populated under a different presentment currency
  const cacheCurrencyMatches =
    !shopCurrency || !cachedCurrency || cachedCurrency === shopCurrency;
  if (cachedProducts && cacheCurrencyMatches) {
    void getRedoProducts({ autocheck, fees, storefrontClient });
    return cachedProducts;
  }

  try {
    return await getRedoProducts({ autocheck, fees, storefrontClient });
  } catch (error) {
    console.error("Error getting redo products", error);
    if (globalThis.redoStorefront) {
      globalThis.redoStorefront.coverageNotShowingReason =
        CoverageNotShowingReason.REDO_PRODUCTS_ERROR;
    }
    throw error;
  }
}
